import type { HTMLAttributes } from 'react'
import { createContext, memo } from 'react'
import { TableRow as UITableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { useSortable } from '@dnd-kit/sortable'

import { RowContext } from '../context/RowContext'
import { TableRowProps } from '../types'
import { PendingUpdateIndicator } from './PendingUpdateIndicator'

// Context to pass drag handle props to child cells
export const DragHandleContext = createContext<{
  isDraggable: boolean
  showDragHandle: boolean
  dragHandleProps: {
    'data-drag-handle': string
  } | null
  isDragging: boolean
  attributes?: ReturnType<typeof useSortable>['attributes']
  listeners?: ReturnType<typeof useSortable>['listeners']
} | null>(null)

type DataTableRowProps = TableRowProps & HTMLAttributes<HTMLTableRowElement>

type SortableRowState = {
  setNodeRef?: (node: HTMLTableRowElement | null) => void
  attributes?: ReturnType<typeof useSortable>['attributes']
  listeners?: ReturnType<typeof useSortable>['listeners']
  isDragging: boolean
}

function DataTableRowContent({
  children,
  isDraggable,
  dragId,
  rowId,
  className,
  onClick,
  dropIndicator,
  showDragHandle = false,
  showPendingUpdateIndicator = true,
  measureElement,
  sortable,
  ...props
}: DataTableRowProps & { sortable?: SortableRowState }) {
  const isDragging = sortable?.isDragging ?? false

  // Disable all transform animations - keep rows in place
  const style = {
    transform: 'none',
    transition: 'none',
  }

  const isDropTarget = dropIndicator?.targetId === dragId
  const dropPosition = dropIndicator?.position
  const dragHandleProps = showDragHandle ? { 'data-drag-handle': 'true' } : null
  const rowContextValue = rowId ? { rowId } : null

  const content = (
    <DragHandleContext.Provider
      value={{
        isDraggable,
        showDragHandle,
        dragHandleProps,
        isDragging,
        attributes: showDragHandle ? sortable?.attributes : undefined,
        listeners: showDragHandle ? sortable?.listeners : undefined,
      }}
    >
      <UITableRow
        ref={element => {
          sortable?.setNodeRef?.(element)
          measureElement?.(element)
        }}
        // Do NOT spread attributes/listeners on the row; only on the handle
        {...props}
        style={{
          ...style,
          userSelect: 'text',
          WebkitUserSelect: 'text',
          ...props.style,
        }}
        className={cn(
          className,
          'group relative',
          isDraggable && 'cursor-default', // handle carries the grab cursor
          isDropTarget &&
            dropPosition === 'above' &&
            'border-t-2 border-t-primary',
          isDropTarget &&
            dropPosition === 'below' &&
            'border-b-2 border-b-primary',
          isDropTarget &&
            dropPosition === 'inside' &&
            'border-2 border-primary/30 bg-primary/10',
        )}
        onClick={onClick}
        onPointerDownCapture={e => {
          const target = e.target as HTMLElement
          // If starting on an editable/input region, don't start drag
          if (
            target.closest(
              'input, textarea, [contenteditable="true"], [data-prevent-drag="true"], [data-editing="true"]',
            )
          ) {
            e.stopPropagation()
          }
        }}
        data-drag-id={dragId}
        data-dragging={isDragging || undefined}
        data-draggable={isDraggable}
        data-row-id={rowId}
        data-drop-position={isDropTarget ? dropPosition : undefined}
      >
        {children}
        {rowId && showPendingUpdateIndicator && (
          <PendingUpdateIndicator rowId={rowId} />
        )}
      </UITableRow>
    </DragHandleContext.Provider>
  )

  if (rowContextValue) {
    return <RowContext value={rowContextValue}>{content}</RowContext>
  }

  return content
}

export const TableRow = memo<DataTableRowProps>(function TableRow(props) {
  return <DataTableRowContent {...props} />
})

export const SortableTableRow = memo<DataTableRowProps>(
  function SortableTableRow({
    children,
    isDraggable,
    dragId,
    rowId,
    className,
    onClick,
    dropIndicator,
    showDragHandle = false,
    showPendingUpdateIndicator = true,
    ...props
  }) {
    const { setNodeRef, attributes, listeners, isDragging } = useSortable({
      id: dragId,
      disabled: !isDraggable,
    })

    return (
      <DataTableRowContent
        {...props}
        isDraggable={isDraggable}
        dragId={dragId}
        rowId={rowId}
        className={className}
        onClick={onClick}
        dropIndicator={dropIndicator}
        showDragHandle={showDragHandle}
        showPendingUpdateIndicator={showPendingUpdateIndicator}
        sortable={{ setNodeRef, attributes, listeners, isDragging }}
      >
        {children}
      </DataTableRowContent>
    )
  },
)

TableRow.displayName = 'DataTable.TableRow'
SortableTableRow.displayName = 'DataTable.SortableTableRow'
